import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ApprovalStage,
  ShiftProductionForm,
  ShiftProductionFormListItem,
} from "../../../shared/shift-production-form/contracts";
import {
  decideShiftForm,
  getShiftForm,
  getShiftFormList,
} from "../shift-production-form/shift-production-form-gateway";

import { loadApprovalFormPages } from "./load-approval-form-pages";
import { partitionApprovalQueue } from "./approval-queue-periods";
import { loadNextApproval } from "./approval-queue-navigation";

export function useShiftFormApprovalWorkspace(
  initialFormId: string | null,
  initialStage: ApprovalStage,
) {
  const stage = initialStage;
  const [items, setItems] = useState<ShiftProductionFormListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [approvalRefreshVersion, setApprovalRefreshVersion] = useState(0);
  const [selectedReadOnly, setSelectedReadOnly] = useState(false);
  const [selected, setSelected] = useState<ShiftProductionForm | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const decisionInFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const listRequest = useRef(0);
  const detailRequest = useRef(0);
  const decisionRequest = useRef(0);

  const loadPendingQueue = useCallback(async (isCurrent: () => boolean = () => true) => {
    const result = await loadApprovalFormPages(
      offset => getShiftFormList("pending", { limit: 6, offset, stage }), isCurrent,
    );
    if (!result) return result;
    const groups = partitionApprovalQueue(result.items);
    return { ...result, items: [...groups.daily, ...groups.overdue, ...groups.other] };
  }, [stage]);

  const reload = useCallback(async () => {
    const request = ++listRequest.current;
    setPhase("loading");
    setError(null);
    try {
      const result = await loadPendingQueue(() => request === listRequest.current);
      if (!result || request !== listRequest.current) return;
      setItems(result.items);
      setTotal(result.total);
      setPhase("ready");
    } catch (reason) {
      if (request !== listRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Onay kuyruğu açılamadı.");
      setPhase("error");
    }
  }, [loadPendingQueue]);

  const open = useCallback(async (formId: string, readOnly = false) => {
    const request = ++detailRequest.current;
    setNotice(null);
    setBusy(true);
    setError(null);
    try {
      const result = await getShiftForm(formId);
      if (request !== detailRequest.current) return;
      setSelectedReadOnly(readOnly);
      setSelected(result.form);
    } catch (reason) {
      if (request !== detailRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Form açılamadı.");
    } finally {
      if (request === detailRequest.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);
  useEffect(() => {
    if (initialFormId) void open(initialFormId);
  }, [initialFormId, open]);
  useEffect(() => () => {
    listRequest.current += 1;
    detailRequest.current += 1;
    decisionRequest.current += 1;
  }, []);

  const decide = async (
    action: "APPROVE",
    explanation: string,
    missingMeasurementAcceptanceReason: string,
  ) => {
    if (!selected || selectedReadOnly || !selected.canDecide || busy || decisionInFlight.current) return;
    decisionInFlight.current = true;
    setNotice(null);
    const request = ++decisionRequest.current;
    listRequest.current += 1;
    setBusy(true);
    setError(null);
    try {
      const result = await decideShiftForm(
        selected.id,
        selected.version,
        selected.revisionNumber,
        action,
        explanation,
        missingMeasurementAcceptanceReason,
      );
      if (request !== decisionRequest.current) return;
      setApprovalRefreshVersion(value => value + 1);
      // The decision has committed. A navigation failure must never retry it.
      try {
        const navigation = await loadNextApproval(result.form.id, async () => {
          const queue = await loadPendingQueue(() => request === decisionRequest.current);
          if (!queue) throw new Error("Onay listesi yenilendi.");
          return queue;
        }, getShiftForm);
        const { queue } = navigation;
        if (request !== decisionRequest.current) return;
        setItems(queue.items);
        setTotal(queue.total);
        setPhase("ready");
        setSelected(navigation.form);
        setNotice(navigation.form ? "Onay kaydedildi. Sıradaki form açıldı." : "Onay kaydedildi. Bu kuyrukta bekleyen form kalmadı.");
      } catch {
        if (request !== decisionRequest.current) return;
        setSelected(result.form);
        setError("Onay kaydedildi. Sıradaki form açılamadı; listeyi yenileyerek devam edin.");
      }
    } catch (reason) {
      if (request !== decisionRequest.current) return;
      setError(
        reason instanceof Error
          ? reason.message
          : "Karar kaydedilemedi. Formu yeniden yükleyin.",
      );
    } finally {
      decisionInFlight.current = false;
      if (request === decisionRequest.current) setBusy(false);
    }
  };

  const closeForm = () => {
    detailRequest.current += 1;
    decisionRequest.current += 1;
    setBusy(false);
    setSelected(null);
  };

  return {
    stage,
    items,
    total,
    selected,
    selectedReadOnly,
    approvalRefreshVersion,
    closeForm,
    phase,
    busy,
    error,
    notice,
    reload,
    open,
    decide,
  };
}
